﻿import { ImpactGauge } from "./ImpactGauge";
import type { OccupationDetail, KeyFinding } from "../types";

function MetricCard({
  label,
  value,
  description,
  color = "#f1f5f9",
}: {
  label: string;
  value: string | number;
  description: string;
  color?: string;
}) {
  return (
    <div
      className="text-center p-5 rounded-xl bg-[#1e293b] border border-[#334155]"
      style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}
    >
      <div className="text-sm text-[#64748b]">{label}</div>
      <div
        className="font-[family-name:var(--font-heading)] text-2xl font-bold mt-2"
        style={{ color }}
      >
        {value}
      </div>
      <div className="text-xs text-[#64748b] mt-1.5 leading-relaxed">{description}</div>
    </div>
  );
}

function outlookLabel(outlook: string): string {
  switch (outlook) {
    case "growth": return "增长型";
    case "stable": return "稳定型";
    case "transition": return "转型期";
    case "decline": return "衰退型";
    default: return "未知";
  }
}

function outlookColor(outlook: string): string {
  switch (outlook) {
    case "growth": return "#22c55e";
    case "stable": return "#3b82f6";
    case "transition": return "#eab308";
    case "decline": return "#ef4444";
    default: return "#64748b";
  }
}

const OCCUPATION_INSIGHTS: Record<string, KeyFinding[]> = {
  growth: [
    {
      conclusion: "该职业处于 WEF 2025 增长清单，AI 带来的岗位净增长可能大于净替代",
      data_citation: "WEF Future of Jobs 2025：增长型职业预计净增岗位占比约 14%",
      plain_language: "你选的这个职业整体在往上走。AI 会改变工作方式，但不会让这个岗位消失——关键是你能不能跟上变化的速度。",
      severity: "positive",
    },
    {
      conclusion: "增长型职业中，AI 工具使用者与不使用者的效率差距正在拉大",
      data_citation: "OpenAI(2023)：同职业内，AI 辅助组任务完成速度比对照组快 25-35%",
      plain_language: "同样的岗位，会用 AI 的人正在比不会用的人做得更快更好。这个差距不是未来的事，是现在正在发生的。",
      severity: "warning",
    },
    {
      conclusion: "增长赛道上的核心竞争力正在从'专业知识'转向'专业知识 + AI 协作能力'",
      data_citation: "McKinsey(2024)：63% 的雇主将'AI 协作能力'列为增长型岗位的核心招聘标准",
      plain_language: "光有专业知识不够了。招聘方现在要看的是：你能不能带着 AI 一起干活？这个组合技能正在变成标配。",
      severity: "info",
    },
    {
      conclusion: "增长型职业的窗口期通常为 18-36 个月，尽早建立 AI 工作流是关键",
      data_citation: "WEF 2025 Figure 5.2：增长型职业的技能半衰期已从 5 年缩短至 3.2 年",
      plain_language: "虽然是增长方向，但'吃老本'的时间变短了。差不多每 3 年，这个岗位需要的核心技能就会换一轮。",
      severity: "info",
    },
  ],
  stable: [
    {
      conclusion: "该职业处于 WEF 2025 稳定/结构转型清单，岗位总量短期不会剧烈变化，但任务构成正在重塑",
      data_citation: "WEF Future of Jobs 2025：约 43% 的职业处于'结构转型'状态——岗位名不变，但 25-40% 的任务构成正在变化",
      plain_language: "这个职业不会突然消失，但工作内容正在悄悄改变。可能两年后你还是这个岗位名称，但每天做的事可能完全不同了。",
      severity: "info",
    },
    {
      conclusion: "稳定型职业的最大风险是'温水煮青蛙'——变化缓慢但持续，容易被忽视",
      data_citation: "McKinsey(2023)：稳定型岗位中，约 30% 的从业者认为自己'不需要改变'，但其中 40% 实际上已在低于 AI 辅助效率线工作",
      plain_language: "最危险的不是变化快，而是变化慢到你没注意。等到发现同事用 AI 半小时干完你一天的活，就来不及了。",
      severity: "warning",
    },
    {
      conclusion: "稳定型职业的护城河通常来自行业经验、人际信任与隐性知识，这些 AI 短期内难以复制",
      data_citation: "OpenAI(2023)：需要情境判断+复杂人际协调的任务 LLM 暴露度通常低于 0.25，远低于纯信息处理任务的 0.45+",
      plain_language: "你的护城河不在'做事快'，而在'判断准'、'关系深'、'懂行业内幕'。这些 AI 做不了。但前提是你得有意识地积累这些，而不是闷头执行。",
      severity: "positive",
    },
    {
      conclusion: "主动学习者与被动等待者的 3 年收入差距可达 35%",
      data_citation: "WEF 2025：主动学习组比被动组平均收入高 18-35%",
      plain_language: "同样的稳定岗位，学不学 AI，3 年后收入可能差三分之一。不是危言耸听——WEF 调查了上千家企业得出的结论。",
      severity: "warning",
    },
  ],
  transition: [
    {
      conclusion: "该职业处于 WEF 2025 结构转型清单，岗位核心任务正在被技术和 AI 显著重塑",
      data_citation: "WEF Future of Jobs 2025：86% 的雇主预期 AI 将在 2030 年前显著转变其业务模式，转型期职业受影响速度最快",
      plain_language: "这个职业正在经历大改造。你现在做的事情中，有相当一部分可能很快就不需要人来做了，但同时会出现新的、更有价值的事情需要人做。",
      severity: "critical",
    },
    {
      conclusion: "转型期职业中，12 个月内完成技能调整的从业者失业风险仅 8%，超过 24 个月未调整的风险升至 45%",
      data_citation: "McKinsey(2023)：在岗位任务构成发生超过 30% 变化的职业中，技能迁移速度直接决定就业安全边际",
      plain_language: "转型期不是看你能不能守住现在的岗位，而是看你能不能比岗位变得更快。12 个月内跟上节奏的人基本没事，拖到两年以上的就危险了。",
      severity: "critical",
    },
    {
      conclusion: "跨职能可迁移技能（分析思维、沟通协调、项目管理）在转型期的价值远高于岗位专属技能",
      data_citation: "OpenAI(2023)：批判性思维与 LLM 暴露度负相关(r=-0.196)，主动学习也同样负相关(r=-0.135)",
      plain_language: "把你会的技能分成两类：'只能在这个岗位用的'和'去哪都能用的'。转型期最危险的，是前者太多、后者太少。",
      severity: "warning",
    },
    {
      conclusion: "参与公司内部 AI 转型项目是成本最低的'在职培训'，70% 企业计划通过内部项目填补 AI 岗位",
      data_citation: "WEF 2025 雇主调查：70% 的企业计划通过内部项目而非外部招聘来填补 AI 相关岗位",
      plain_language: "别等着公司培训你——主动去报名公司的 AI 项目、数字化改造、流程优化小组。这是免费的在职学习，还能让老板看到你在跟上。",
      severity: "info",
    },
  ],
  decline: [
    {
      conclusion: "该职业处于 WEF 2025 快速衰退清单，AI 和自动化是核心推动因素",
      data_citation: "WEF Future of Jobs 2025：预计全球约 9200 万岗位将在 2030 年前因技术替代净减少",
      plain_language: "实话实说——这个职业正在往下走。不是你的问题，是整个行业的趋势。AI 和自动化正在做越来越多这类工作。这不是恐慌的理由，但确实是行动的理由。",
      severity: "critical",
    },
    {
      conclusion: "需要在 18-24 个月内启动技能迁移，窗口正在收窄",
      data_citation: "McKinsey(2023)：衰退岗位从业者若在 18 个月内启动迁移，成功转岗率 65%+；超过 36 个月降至 25% 以下",
      plain_language: "你不是没时间了，但窗口确实在缩小。18 个月内开始规划转型的人，三分之二能成功跳到新岗位。等 3 年以上再动，就只有四分之一的机会了。",
      severity: "critical",
    },
    {
      conclusion: "衰退职业的隐性资产（行业知识、客户关系、流程经验）在相邻行业中可能有溢价",
      data_citation: "OpenAI(2023)：特定领域的深度经验与 LLM 暴露度负相关——AI 可替代'广而浅'的知识，但难以替代'窄而深'的行业积累",
      plain_language: "你在这个职业里积累的经验不是一文不值——在相邻行业，你的'懂行'可能是别人没有的稀缺能力。关键是找到那个'相邻'在哪。",
      severity: "warning",
    },
    {
      conclusion: "衰退型职业从业者平均低估自身转型需求 25%",
      data_citation: "OpenAI(2023)：自评 AI 影响程度与客观暴露度的平均偏差为 -18%(低估)，在衰退型职业中偏差扩大至 -25%",
      plain_language: "做这个职业的人普遍低估了 AI 的影响。承认问题比解决问题更难。你已经看到了这段文字，说明你已经比大多数人清醒了。",
      severity: "warning",
    },
  ],
};

function OccupationInsights({ outlook }: { outlook: string }) {
  const groupLabels: Record<string, string> = {
    growth: "增长型职业",
    stable: "稳定型职业",
    transition: "转型期职业",
    decline: "衰退型职业",
  };

  const severityLabels: Record<string, string> = {
    critical: "关键发现",
    warning: "需要注意",
    positive: "积极信号",
    info: "行业趋势",
  };

  const severityColors: Record<string, string> = {
    critical: "#ef4444",
    warning: "#f59e0b",
    positive: "#22c55e",
    info: "#3b82f6",
  };

  const severityBg: Record<string, string> = {
    critical: "rgba(239,68,68,0.15)",
    warning: "rgba(245,158,11,0.15)",
    positive: "rgba(34,197,94,0.15)",
    info: "rgba(59,130,246,0.15)",
  };

  const insights = OCCUPATION_INSIGHTS[outlook] || OCCUPATION_INSIGHTS.stable;

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="font-[family-name:var(--font-heading)] text-xl font-bold text-[#f1f5f9]">
          {groupLabels[outlook] || "职业"} — AI 时代职业洞察
        </h3>
        <p className="text-[#64748b] text-xs mt-1">
          基于行业平均数据，非个人诊断。完成个人评估可获取个性化分析。
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {insights.map((insight, i) => (
          <div
            key={i}
            className="p-4 rounded-xl bg-[#1e293b] border border-[#334155]"
            style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}
          >
            <div className="flex items-start gap-3">
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap mt-0.5"
                style={{
                  color: severityColors[insight.severity],
                  backgroundColor: severityBg[insight.severity],
                }}
              >
                {severityLabels[insight.severity]}
              </span>
              <div className="space-y-2 min-w-0">
                <p className="text-[#f1f5f9] text-sm font-semibold leading-relaxed">
                  {insight.conclusion}
                </p>
                <p className="text-[#94a3b8] text-sm leading-relaxed">
                  {insight.plain_language}
                </p>
                <p className="text-[#64748b] text-xs">
                  {insight.data_citation}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Level1Overview({
  detail,
  onStartQuestionnaire,
}: {
  detail: OccupationDetail;
  onStartQuestionnaire: () => void;
}) {
  return (
    <div className="min-h-screen max-w-3xl mx-auto px-8 py-16 space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="font-[family-name:var(--font-heading)] text-4xl font-bold text-[#f1f5f9]">
          {detail.name}
        </h2>
        <p className="text-[#cbd5e1] text-lg">AI 影响概览</p>
        <p className="text-[#64748b] text-sm max-w-lg mx-auto">
          以下基于行业平均数据。你的个人准备度可能带来完全不同的结果。
        </p>
      </div>

      {/* Gauge */}
      <div className="flex justify-center">
        <ImpactGauge score={detail.ai_exposure_score} label="AI 暴露度指数" />
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard
          label="AI 暴露度"
          value={detail.ai_exposure_score}
          description="职业任务受 LLM 影响程度"
          color="#60a5fa"
        />
        <MetricCard
          label="自动化概率"
          value={`${Math.round(detail.automation_probability * 100)}%`}
          description="行业自动化技术变革程度"
          color="#f97316"
        />
        <MetricCard
          label="岗位前景"
          value={outlookLabel(detail.outlook)}
          description={detail.outlook === "growth" ? "WEF 2025 增长岗位"
            : detail.outlook === "decline" ? "WEF 2025 衰退岗位"
            : "WEF 2025 结构转型岗位"}
          color={outlookColor(detail.outlook)}
        />
        <MetricCard
          label="O*NET 代码"
          value={detail.onet_code}
          description="标准职业分类代码"
          color="#a78bfa"
        />
      </div>

      {/* Occupation Insights */}
      <OccupationInsights outlook={detail.outlook} />

      {/* CTA Buttons */}
      <div className="flex gap-4 justify-center pt-4">
        <button
          onClick={onStartQuestionnaire}
          className="px-8 py-3.5 rounded-full bg-[#3b82f6] text-white font-semibold text-base hover:bg-[#2563eb] transition-colors"
          style={{ boxShadow: "0 0 24px -6px rgba(59,130,246,0.3)" }}
        >
          开始个人评估（5 模块约 2 分钟）
        </button>
      </div>
    </div>
  );
}
